import { CenteredSpin, DropdownSelector2, IconButton, TimeRangePicker } from '../../../ui'
import { FC, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import style from './style.m.less'
import { Input } from 'antd'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import { getDictionaries, selectDictionariesOptions } from '../../../../store/dictionaries'
import TagSelector from '../TagSelector/TagSelector'
import { ELoading } from '../../../../store/dictionaries/interfaces'
import { EGoStatusType, EUserRole, goStatusTypeMap } from '../../../../constants/approval'
import classNames from 'classnames'
import { DropdownSelector } from './components'
import moment from 'moment'

export interface IFilterValue {
    renewableEnergyType: number[]
    totalPowerFrom: number
    totalPowerTo: number
    lastCheckDate: [moment.Moment, moment.Moment] | null
    qualificationDate: [moment.Moment, moment.Moment] | null
    regionType: number
    statusType: EGoStatusType
}

interface IFilters {
    values: IFilterValue
    onChange(values: IFilterValue): void
    name: string
    onChangeUserName: (name: string) => void
    isAuth: boolean
}
const UNIT_IDS = {
    MW: 'МВт',
    KW: 'КВт',
} as const

const Filters: FC<IFilters> = ({ values, onChange, name, onChangeUserName, isAuth }) => {
    const { t, i18n } = useTranslation()

    const locale = useMemo(() => {
        const lang = i18n.language.toLowerCase()
        return lang.includes('ru') ? 'ru' : 'en'
    }, [i18n.language])

    const userRole: EUserRole = useAppSelector((state) => state.user.data.user_role)

    const showNameSearch = [
        EUserRole.Administrator,
        EUserRole.Operator,
        EUserRole.SuperAdmin,
    ].includes(userRole)

    const {
        renewableEnergyType,
        totalPowerFrom,
        totalPowerTo,
        lastCheckDate,
        qualificationDate,
        regionType,
        statusType,
    } = values

    const isLoading = useAppSelector((state) => state.dictionaries.loading)

    const dispatch = useAppDispatch()
    useEffect(() => {
        if (isLoading !== ELoading.Loaded) {
            dispatch(getDictionaries())
        }
    }, [])

    const goStatusTypeOptions = useMemo(
        () => [
            {
                id: EGoStatusType.All,
                value: EGoStatusType.All,
                text: t('filters.statusTypeAll') || goStatusTypeMap.get(EGoStatusType.All),
            },
            {
                id: EGoStatusType.Blocked,
                value: EGoStatusType.Blocked,
                text: t('filters.statusTypeBlocked') || goStatusTypeMap.get(EGoStatusType.Blocked),
            },
            {
                id: EGoStatusType.NotBlocked,
                value: EGoStatusType.NotBlocked,
                text:
                    t('filters.statusTypeNotBlocked') ||
                    goStatusTypeMap.get(EGoStatusType.NotBlocked),
            },
        ],
        [t],
    )

    const unitTypes = useMemo(
        () => [
            { value: t('generatorCard.scale'), id: UNIT_IDS.MW },
            { value: t('generatorCard.scale3'), id: UNIT_IDS.KW },
        ],
        [t],
    )
    const [powerMeasurement, setPowerMeasurement] = useState<string>(UNIT_IDS.MW)

    const dictionaries = useAppSelector(selectDictionariesOptions)

    const { regionsOptions, renewableEnergyTypesOptions } = dictionaries ?? {}

    const realRegionsOptions = useMemo(
        () =>
            regionsOptions
                ? [
                      {
                          id: 0,
                          value: 0,
                          text: t('filters.regionAny'),
                          label: t('filters.regionAny'),
                      },
                      ...regionsOptions,
                  ]
                : [
                      {
                          id: 0,
                          value: 0,
                          text: t('filters.regionAny'),
                          label: t('filters.regionAny'),
                      },
                  ],
        [regionsOptions, t],
    )

    if (!dictionaries || isLoading === ELoading.Loading) {
        return <CenteredSpin />
    }

    const displayPowerMeasurement =
        unitTypes.find((unit) => unit.id === powerMeasurement)?.value || unitTypes[0]?.value

    const realTotalPowerFrom =
        powerMeasurement === UNIT_IDS.MW ? totalPowerFrom / 1000 : totalPowerFrom
    const realTotalPowerTo = powerMeasurement === UNIT_IDS.MW ? totalPowerTo / 1000 : totalPowerTo

    return (
        <div className={style.filtersMain}>
            {showNameSearch && (
                <>
                    <div>{t('filters.labelNameZGO')}</div>
                    <Input
                        value={name}
                        onChange={(event) => onChangeUserName(event.target.value)}
                        className={style.input}
                        placeholder={t('filters.placeholderNameZGO')}
                    />
                </>
            )}
            <div className={classNames({ [style.filterTitle]: showNameSearch })}>
                {t('filters.powerType')}
            </div>
            <TagSelector
                value={renewableEnergyType}
                onChange={(renewableEnergyType) =>
                    onChange({
                        ...values,
                        renewableEnergyType,
                    })
                }
                customStyle={{ marginTop: 0 }}
                options={renewableEnergyTypesOptions}
                showSearch={false}
                currentLang={locale}
            />
            <div className={style.filterTitle}>{t('filters.power')}</div>
            <DropdownSelector
                value={displayPowerMeasurement}
                onChange={(selectedValue: string) => {
                    const selectedUnit = unitTypes.find((unit) => unit.value === selectedValue)
                    if (selectedUnit) {
                        setPowerMeasurement(selectedUnit.id)
                    }
                }}
                options={unitTypes}
                customStyle={{ width: '100%' }}
            />
            <div className={style.inputWrapper}>
                <Input
                    value={realTotalPowerFrom}
                    onChange={(e) => {
                        const value = Math.max(+e.target.value || 0, 0)
                        onChange({
                            ...values,
                            totalPowerFrom: powerMeasurement === UNIT_IDS.MW ? value * 1000 : value,
                        })
                    }}
                    className={style.input}
                />
                <Input
                    value={realTotalPowerTo}
                    onChange={(e) => {
                        const value = Math.max(+e.target.value || 0, 0)
                        onChange({
                            ...values,
                            totalPowerTo: powerMeasurement === UNIT_IDS.MW ? value * 1000 : value,
                        })
                    }}
                    className={style.input}
                />
            </div>
            <div className={style.filterItem}>
                <div className={style.label}>{t('filters.qualifyDate')}</div>
                <TimeRangePicker
                    value={qualificationDate}
                    onChange={(qualificationDate) =>
                        onChange({
                            ...values,
                            qualificationDate,
                        })
                    }
                />
            </div>
            <div className={style.filterItem}>
                <div className={style.label}>{t('filters.lastCheckDate')}</div>
                <TimeRangePicker
                    value={lastCheckDate}
                    onChange={(lastCheckDate) =>
                        onChange({
                            ...values,
                            lastCheckDate,
                        })
                    }
                />
            </div>
            <div className={style.filterTitle}>{t('filters.labelRegion')}</div>
            <DropdownSelector2
                value={regionType}
                options={realRegionsOptions}
                placeholder={t('filters.chooseRegion')}
                customStyle={{ width: '100%' }}
                onChange={(regionType) => {
                    onChange({
                        ...values,
                        regionType,
                    })
                }}
                currentLang={locale}
            />
            {isAuth && (
                <>
                    <div className={style.filterTitle}>{t('filters.labelStatus')}</div>
                    <DropdownSelector2
                        value={statusType}
                        options={goStatusTypeOptions}
                        onChange={(statusType) => {
                            onChange({
                                ...values,
                                statusType,
                            })
                        }}
                        customStyle={{ width: '100%' }}
                    />
                </>
            )}

            <div className={style.resetButton}>
                <IconButton
                    onClick={() => {
                        onChange({
                            renewableEnergyType: [],
                            totalPowerFrom: 0,
                            totalPowerTo: 0,
                            lastCheckDate: null,
                            qualificationDate: null,
                            regionType: 0,
                            statusType: EGoStatusType.All,
                        })
                        onChangeUserName('')
                        setPowerMeasurement(UNIT_IDS.MW)
                    }}
                >
                    {t('ownerCard.resetFilters')}
                </IconButton>
            </div>
        </div>
    )
}

export default Filters
